import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isNil } from 'lodash';
import { filter, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { ModalComponent, NotificationsService } from '@geekbears/ngx-gb-ui-kit';

import {
  changeRequestActionTypeSuccessAction,
  createRequestTypeFailureAction,
  createRequestTypeSuccessAction,
  deleteRequestTypeFailureAction,
  deleteRequestTypeSuccessAction,
  RequestsTypeActions,
  updateRequestTypeFailureAction,
  updateRequestTypeSuccessAction,
  changeRequestActionTypeLoadingSelector,
  createRequestTypeLoadingSelector,
  deleteRequestTypeLoadingSelector,
  updateRequestTypeLoadingSelector,
} from '@modules/company-manager/requests-type/application';
import { RequestAction } from '@modules/company-manager/requests-type/domain';

@Component({
  selector: 'app-request-type-modals',
  templateUrl: './request-type-modals.modal.html',
  styleUrls: ['./request-type-modals.modal.scss'],
})
export class RequestTypeModals
  extends ModalComponent
  implements OnInit, OnDestroy
{
  /**
   * @destroy$ is used to unsubscribe from all subscriptions
   */
  destroy$: Subject<void> = new Subject();
  modalType: string = '';
  action: string = '';
  form!: FormGroup;
  request!: RequestAction;
  createRequestTypeLoading$: Observable<boolean | undefined | null>;
  deleteRequestTypeLoading$: Observable<boolean | undefined | null>;
  editRequestTypeLoading$: Observable<boolean | undefined | null>;
  _companyId: string = '';
  isDisabled: boolean = false;

  changingRequestActionType$: Observable<boolean | undefined>;
  requestActionType: string = 'unanimous';

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private dialogRef: MatDialogRef<RequestTypeModals>,
    private fb: FormBuilder,
    private store$: Store,
    private actions$: Actions,
    private notificationsService: NotificationsService
  ) {
    super();
    if (data) {
      this._companyId = data.companyId;
      this.modalType = data.type;
      this.request = data.request;
      if (data.requestActionType)
        this.requestActionType = data.requestActionType;

      this.form = this.fb.group({
        action: [
          data?.request?.actionName ? data?.request?.actionName : '',
          Validators.required,
        ],
      });
    }
    this.changingRequestActionType$ = this.store$.select(
      changeRequestActionTypeLoadingSelector
    );

    // CREATING request selector
    this.createRequestTypeLoading$ = this.store$
      .select(createRequestTypeLoadingSelector)
      .pipe(
        tap((status) => {
          if (status) this.isDisabled = true;
        })
      );

    // DELETING request selector
    this.deleteRequestTypeLoading$ = this.store$
      .select(deleteRequestTypeLoadingSelector)
      .pipe(
        tap((status) => {
          if (status) this.isDisabled = true;
        })
      );

    // UPDATING request selector
    this.editRequestTypeLoading$ = this.store$
      .select(updateRequestTypeLoadingSelector)
      .pipe(
        tap((status) => {
          if (status) this.isDisabled = true;
        })
      );
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  ngOnInit(): void {
    this.onFailureActions();
    this.onSuccessAction();
  }
  closeModal() {
    this.dialogRef.close();
  }

  createRequestType() {
    if (this.isDisabled == false)
      this.store$.dispatch(
        RequestsTypeActions.createRequestType({
          requestType: {
            _company: this._companyId,
            actionName: this.form.get('action')?.value,
          },
        })
      );
  }
  editRequestType() {
    if (this.isDisabled == false)
      this.store$.dispatch(
        RequestsTypeActions.updateRequestType({
          id: this.request.id ? this.request.id : '',
          action: this.form.get('action')?.value,
        })
      );
  }
  deleteRequestType() {
    if (this.isDisabled == false)
      this.store$.dispatch(
        RequestsTypeActions.deleteRequestType({
          requestsTypeId: this.request?.id,
        })
      );
  }
  onInputChange(event: any) {
    this.action = event.target.value;
  }
  showNotification(text: string, isSuccess?: boolean) {
    this.notificationsService.success(text, {
      duration: 3000,
      icon: 'check-circle',
    });
    this.closeModal();
  }

  /**
   * Handle On Failure Actions
   */
  onFailureActions() {
    this.actions$
      .pipe(
        ofType(
          createRequestTypeFailureAction,
          updateRequestTypeFailureAction,
          deleteRequestTypeFailureAction
        ),
        map((action) => {
          setTimeout(() => {
            this.isDisabled = false;
          }, 1500);
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          if (errorMessage) {
            this.notificationsService.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  /**
   * Update Request Action Type
   */
  updateRequestActionsType() {
    if (this.requestActionType)
      this.store$.dispatch(
        RequestsTypeActions.changeRequestActionType({
          companyId: this._companyId,
          data: {
            approvalType: this.requestActionType,
          },
        })
      );
  }

  /**
   * Handle On Success Actions
   */
  onSuccessAction() {
    this.actions$
      .pipe(
        ofType(
          updateRequestTypeSuccessAction,
          changeRequestActionTypeSuccessAction,
          deleteRequestTypeSuccessAction,
          createRequestTypeSuccessAction
        ),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action) => {
          setTimeout(() => {
            this.isDisabled = false;
          }, 1500);

          // on UPDATE request action name
          if (action.type.toLocaleLowerCase().includes('update request type'))
            this.showNotification('Request Type Updated Successfully');

          // on UPDATE request type configuration
          if (
            action.type.toLocaleLowerCase().includes('change request action')
          ) {
            if (action.response.data && action.response.data.approvalType) {
              this.requestActionType = action.response.data.approvalType;

              this.notificationsService.success('Updated Successfully', {
                duration: 3000,
                icon: 'check-circle',
              });

              this.dialogRef.close({
                type: this.modalType,
                requestType: this.requestActionType,
              });
            }
          }
          // on DELETE request
          if (action.type.toLocaleLowerCase().includes('delete request'))
            this.modalType = 'deleteRequestSuccess';

          // on CREATE request
          if (action.type.toLocaleLowerCase().includes('create request'))
            this.showNotification('Request Type Created Successfully');
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }
}
